// This file is automatically generated. Do not edit it directly.
import { createClient } from "@supabase/supabase-js";
import type { Database } from "./types";
import { supabaseAuthStorage } from "@/lib/auth/auth-storage";
import { createThrottledFetch } from "@/lib/supabase-fetch-throttle";
import { isProductionSupabaseEgressBlocked } from "@/lib/supabase-egress-guard";

const throttledFetch = createThrottledFetch(fetch);
const devEgressQuarantine = isProductionSupabaseEgressBlocked();

function sanitizeEnvValue(value: string | undefined | null): string {
  if (!value) return "";
  let normalized = value.trim();
  if (
    (normalized.startsWith('"') && normalized.endsWith('"')) ||
    (normalized.startsWith("'") && normalized.endsWith("'"))
  ) {
    normalized = normalized.slice(1, -1);
  }
  return normalized.trim();
}

export function getResolvedConfig() {
  const isBrowser = typeof window !== "undefined";

  // 1. Runtime window / global injection
  const windowEnv = isBrowser ? ((window as any).__ENV__ || (window as any).ENV || {}) : {};

  // 2. LocalStorage override (if explicitly saved by admin)
  let storedUrl = "";
  let storedKey = "";
  let storedProjectId = "";
  if (isBrowser) {
    try {
      storedUrl = localStorage.getItem("VITE_SUPABASE_URL") || "";
      storedKey = localStorage.getItem("VITE_SUPABASE_PUBLISHABLE_KEY") || "";
      storedProjectId = localStorage.getItem("VITE_SUPABASE_PROJECT_ID") || "";
    } catch {
      // Ignore localStorage access restrictions
    }
  }

  // 3. Build-time environment variable
  const metaEnv = typeof import.meta !== "undefined" && import.meta.env ? import.meta.env : (typeof process !== "undefined" && process.env ? process.env : {});
  const envUrl = sanitizeEnvValue(metaEnv.VITE_SUPABASE_URL as string | undefined);
  const envKey = sanitizeEnvValue(
    (metaEnv.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined) ||
    (metaEnv.VITE_SUPABASE_ANON_KEY as string | undefined)
  );
  const envProjectId = sanitizeEnvValue(metaEnv.VITE_SUPABASE_PROJECT_ID as string | undefined);

  const DEFAULT_SUPABASE_URL = "https://backend.arivahly.tech";
  const DEFAULT_SUPABASE_KEY = "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJyb2xlIjoiYW5vbiIsImlzcyI6ImF2cy1tdGotZXJwIiwicmVmIjoiYXZzLXBvc3RncmVzIiwiZmlybV9pZCI6IjAwMDAwMDAwLTAwMDAtMDAwMC0wMDAwLTAwMDAwMDAwMDAwMSIsImlhdCI6MTcwMDAwMDAwMCwiZXhwIjoyMjAwMDAwMDAwfQ.Zns307ESh1epGanVxrJVwrIWiSaMarU3MK5GlArABYI";
  const DEFAULT_SUPABASE_PROJECT_ID = "avs-postgres";

  let resolvedUrl = sanitizeEnvValue(windowEnv.VITE_SUPABASE_URL) || storedUrl || envUrl || DEFAULT_SUPABASE_URL;
  let resolvedKey = sanitizeEnvValue(windowEnv.VITE_SUPABASE_PUBLISHABLE_KEY || windowEnv.VITE_SUPABASE_ANON_KEY) || storedKey || envKey || DEFAULT_SUPABASE_KEY;
  let resolvedProjectId = sanitizeEnvValue(windowEnv.VITE_SUPABASE_PROJECT_ID) || storedProjectId || envProjectId || DEFAULT_SUPABASE_PROJECT_ID;

  // Discard dead legacy URLs (egress-blocked / retired projects)
  if (
    resolvedUrl.includes("xrvsvzfqjptzbxjscnvf") ||
    resolvedUrl.includes("vqsrdemjiehzykexkcwo") ||
    resolvedUrl.includes("dqgrrafuoxaorvyrcuuh") ||
    resolvedUrl.includes("slbvphnxfcypeinclgmw") || resolvedUrl.includes("fzslatzpoqgwpivpwnvb") || resolvedUrl.includes("supabase.co")
  ) {
    resolvedUrl = DEFAULT_SUPABASE_URL;
    resolvedKey = DEFAULT_SUPABASE_KEY;
    resolvedProjectId = DEFAULT_SUPABASE_PROJECT_ID;
  }

  return {
    url: resolvedUrl,
    key: resolvedKey,
    projectId: resolvedProjectId || DEFAULT_SUPABASE_PROJECT_ID,
  };
}

export function isSupabaseConfigured(): boolean {
  const { url, key } = getResolvedConfig();
  return !!url && !!key && !url.includes("placeholder") && (url.startsWith("http://") || url.startsWith("https://"));
}

function createSupabaseClient() {
  const { url: supabaseUrl, key: supabasePublishableKey } = getResolvedConfig();

  return createClient<Database>(supabaseUrl, supabasePublishableKey, {
    auth: {
      storage: typeof window !== "undefined" ? supabaseAuthStorage : undefined,
      persistSession: true,
      // Hourly token refresh against production from localhost was ~347 auth req/min.
      autoRefreshToken: !devEgressQuarantine,
      detectSessionInUrl: true,
    },
    realtime: devEgressQuarantine
      ? { params: { eventsPerSecond: 1 } }
      : undefined,
    global: {
      fetch: (input, init) => {
        const timeoutSignal = AbortSignal.timeout(25_000);
        const signal =
          init?.signal && typeof AbortSignal.any === "function"
            ? AbortSignal.any([init.signal, timeoutSignal])
            : (init?.signal ?? timeoutSignal);
        return throttledFetch(input, { ...init, signal });
      },
    },
  });
}

let supabaseClient: ReturnType<typeof createSupabaseClient> | undefined;

export function getRawSupabaseClient(): ReturnType<typeof createSupabaseClient> {
  if (!supabaseClient) supabaseClient = createSupabaseClient();
  return supabaseClient;
}

export function getSupabaseClient(): ReturnType<typeof createSupabaseClient> {
  return getRawSupabaseClient();
}
